/**
 * contentDisposition — read a download's filename off the response header.
 *
 * ## Matched to the REAL emitter, not to a spec read in the abstract
 *
 * The ecosystem's SSOT is `@puffin-core/exports`
 * (`lib/streaming.js:32-39`, `buildContentDispositionAttachment`). Measured, it
 * emits TWO forms:
 *
 *     pure ASCII  attachment; filename="report.csv"          ← inner quotes backslash-escaped
 *     non-ASCII   attachment; filename="ascii_fallback_.csv"; filename*=UTF-8''<percent-encoded>
 *
 * The second form exists because HTTP header values are Latin-1, so a filename
 * containing an em-dash (a user-named custom report) would throw `ERR_INVALID_CHAR`
 * at `res.setHeader`. The real name can only travel in the RFC 5987 parameter, and
 * the legacy `filename` carries an underscored sanitisation of it.
 *
 * 🔴 **So `filename*` must WIN.** port-web's existing hand-rolled reader
 * (`callRecordingApi.js:83-85`, `/filename="?([^";]+)"?/`) matches only the legacy
 * parameter, which means every non-ASCII export in that app downloads under its
 * mangled fallback name. Reading the emitter is what found that; reading RFC 6266
 * alone would not have.
 *
 * `inline; filename="…"` is read too — `port-api`'s fax surface uses it
 * (`services/fax/FaxStorageService.js:35`).
 *
 * @module @puffin-core/api-client/facade/contentDisposition
 */

/** RFC 5987 `filename*=<charset>'<lang>'<percent-encoded>`. Checked FIRST — it wins. */
const EXTENDED_PARAM_RE = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i;

/** RFC 6266 `filename="…"`, tolerant of the backslash-escaped inner quotes the emitter writes. */
const QUOTED_PARAM_RE = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i;

/** The bare `filename=report.csv` form some hand-rolled senders emit. */
const BARE_PARAM_RE = /filename\s*=\s*([^;]+)/i;

/**
 * An empty filename is not a filename.
 *
 * Returning `''` would sail through a `filename || fallback` guard at the call site
 * as a real answer and produce a nameless download.
 *
 * @param {string} value
 * @returns {string|null}
 */
function emptyToNull(value) {
  return value.length > 0 ? value : null;
}

/**
 * Percent-decode an RFC 5987 value, surviving a malformed encoding.
 *
 * `decodeURIComponent('bad%ZZ')` throws `URIError`. A download must not fail
 * because a filename was mis-encoded upstream — the raw token is still a usable
 * answer, and a usable wrong name beats no file.
 *
 * @param {string} raw
 * @returns {string}
 */
function decodeExtendedValue(raw) {
  try {
    return decodeURIComponent(raw);
  } catch (error) {
    // Narrowed (SWALLOWED-CATCH U3): only the malformed encoding is expected; anything else is a
    // defect and surfaces.
    if (!(error instanceof URIError)) throw error;
    return raw;
  }
}

/**
 * Undo the RFC 6266 backslash escaping the emitter applies to inner quotes.
 *
 * @param {string} raw
 * @returns {string}
 */
function unescapeQuotedValue(raw) {
  return raw.replace(/\\(.)/g, '$1');
}

/**
 * The filename a `Content-Disposition` header names, or null.
 *
 * @param {*} value - The raw header value; anything non-string yields null.
 * @returns {string|null}
 */
export function readContentDispositionFilename(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const extended = EXTENDED_PARAM_RE.exec(value);
  if (extended) {
    return emptyToNull(decodeExtendedValue(extended[1].trim()));
  }

  const quoted = QUOTED_PARAM_RE.exec(value);
  if (quoted) {
    return emptyToNull(unescapeQuotedValue(quoted[1]).trim());
  }

  const bare = BARE_PARAM_RE.exec(value);
  if (bare) {
    return emptyToNull(bare[1].trim());
  }

  return null;
}
